/**
 * Dev-sync J1+J2 — the psychrometric chart popup (header-launched,
 * full-screen modal, AHU board only).
 *
 * React port of legacy `static/mvc/psychrometric_chart/view/
 * psychrometric_chart.js` (the Backbone view) + its 69-line template. The
 * host element reproduces the view's own $el layout: one
 * `.psychrometric-chart-host` containing BOTH the `.psychrometric-chart-popup
 * .popup-overlay` overlay and `#psy-floating-icon` — visibility on each is
 * the legacy `.active` class, styled by the 114 psy- rules already merged
 * into common.css (survey §C2; markup/classes kept verbatim so that CSS
 * lands for free).
 *
 * J1 = READ-ONLY chart: header launch, shell, Plotly render, legend, sidebar,
 * client-side season filter, minimize→floating icon, restore-with-refresh.
 * J2 = ANNOTATIONS (state points): click-anywhere-to-add, click-to-edit, the
 * inline editor (Enter/Escape/blur/delete), Clear All, hover-label toggle,
 * persistence via the psychrometric_annotations endpoints.
 * J3a = #psy-download-btn — chart + sidebar-table composite PNG (exportPng.ts).
 * J4  = #psy-sync-toggle (onSyncToggle js:607-630) — the ONLY UI that flips
 *       AHUUnit.synchronize_air_conditions (survey §A2).
 * J5  = the sidebar ACTIONS: SYNCHRONIZE (synchronizeRow js:1361-1450) and
 *       the completed jumpToSection re-rate handoff (PSY_PENDING_RERATE_KEY
 *       write + the Redux rating-dialog activation replacing legacy's DOM
 *       click, js:1458-1538); the selectors consume via pendingRerate.ts.
 *
 * ★ FETCH DISCIPLINE (survey §A1 gotcha 6 — the GET writes to the DB): the
 * chart-data fetch happens at EXACTLY the legacy call points — show() on
 * first open, and the explicit refreshes (restore's background refresh, the
 * sync-toggle OFF→ON refetch). Annotation mutations NEVER refetch chart_data
 * (legacy patches this.data.annotations locally and re-renders —
 * js:2265-2399); their persistence calls are the dedicated annotation
 * endpoints only.
 *
 * updateShell's animation-dodging DOM patching (js:493-518) has no port:
 * React never re-creates the dialog node across the loading→loaded swap
 * (state-driven children inside a stable element), so the `.active …
 * animation-name` re-fire it works around cannot happen.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import { ahuBoardApi } from 'app/services/ahuBoardApi';
import { store, type RootState } from 'app/store';
import { openRatingRequest } from 'app/slices/ahuBoardSlice';
import { ahuBoardBus } from '../bus/ahuBoardBus';
import {
  clearAllAnnotations,
  computeStaleBySeason,
  createAnnotation,
  deleteAnnotation,
  fetchChartData,
  patchAnnotationLabel,
  patchSynchronizeAirConditions,
  postCommitAirConditions,
  type PsyAnnotation,
  type PsyChartData,
  type PsySeason,
  type PsySeasonFilter,
  type PsySidebarRow,
} from './psychrometricApi';
import { clearPendingRerate, writePendingRerate } from './pendingRerate';
import {
  getPsyStreamVisibility,
  setPsyPopupState,
  setPsyStreamVisibility,
} from './psychrometricStorage';
import type { PsyStreamVisibility, PsyToggleableStream } from './streamVisibility';
import { usePlotly, type PlotlyChartNode } from './usePlotly';
import { buildTraces, UDA_KINDS } from './chartTraces';
import { buildLayout, PLOT_CONFIG } from './chartLayout';
import { declutterLabels } from './declutter';
import { findAnnotationNear, pixelToData, dataToPixel } from './annotations';
import { exportChartAsPng } from './exportPng';
import Legend from './Legend';
import { SidebarTable, SidebarTabsBar, rowKey } from './SidebarTable';
import PsychrometricFloatingIcon from './PsychrometricFloatingIcon';

interface PsychrometricChartPopupProps {
  /** The board's AHU id (legacy getAhuId() read #ahu-tabs[data-id] — the
   * route param is the same value; null keeps the legacy no-AHU guard). */
  ahuId: number | null;
  /**
   * How this create-once mount came to exist (usePsychrometrics):
   *  - 'show'      — the header button (openPsychrometricChart → show()).
   *  - 'minimized' — page-load replay of a stored 'minimize'
   *    (header.js:250-266): SHELL ONLY, floating icon on, NO data fetch;
   *    data loads lazily on restore().
   */
  boot: 'show' | 'minimized';
  /** Bumped by every later header-button click → restore() (header.js:555-559
   * routes every subsequent open through restore, not show). */
  openNonce: number;
}

/** Plotly hover event point — the slice bindAnnotationHoverLabelToggle reads. */
interface PlotlyHoverPoint {
  curveNumber: number;
  pointNumber: number;
  data?: { name?: string };
}

const PsychrometricChartPopup = ({ ahuId, boot, openNonce }: PsychrometricChartPopupProps) => {
  const [active, setActive] = useState(false);
  const [iconActive, setIconActive] = useState(boot === 'minimized');
  const [loading, setLoading] = useState(false);
  /** The first chart_data fetch failed — the body would otherwise be a blank
   * white dialog with nothing in it (the catch below only stops the spinner). */
  const [loadFailed, setLoadFailed] = useState(false);
  /**
   * Plotly has drawn the plot at least once. Data landing is NOT the end of
   * the wait: the vendored 1 MB plotly-basic still has to load and render, and
   * until it does the canvas is an empty white rectangle next to a populated
   * sidebar. The spinner stays over the plot area for that window. Never reset
   * — a later re-render (background refresh, season switch) redraws over the
   * chart that is already on screen, so there is nothing empty to cover.
   */
  const [chartReady, setChartReady] = useState(false);
  const [data, setData] = useState<PsyChartData | null>(null);
  const [status, setStatus] = useState('');
  const [season, setSeason] = useState<PsySeasonFilter>('both');
  // Sidebar tab — independent of the chart's own season selector (which has
  // a third "both" option the table can't show at once); synced to a
  // specific season in onSeasonChange, left alone on 'both' (js:317-323).
  const [sidebarSeason, setSidebarSeason] = useState<PsySeason>('summer');
  // Collapsed path groups, keyed by path.name — survives re-renders (js:324-329).
  const [collapsedPaths, setCollapsedPaths] = useState<Record<string, boolean>>({});
  // Selected sidebar row — rowKey(component_kind, component_id), NOT
  // section_id (js:331-340).
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);
  // The legend's air-stream checkboxes. Client-side like the season filter —
  // every stream ships in every payload, so unchecking one is a re-render,
  // never a refetch. Seeded from localStorage (lazy initialiser: one read,
  // at mount, not on every render) and written back on each toggle.
  const [streamVisibility, setStreamVisibility] =
    useState<PsyStreamVisibility>(getPsyStreamVisibility);

  // ---- J2 annotation state ----------------------------------------------
  // "Add State Point" armed (js:315 annotationMode).
  const [annotationMode, setAnnotationMode] = useState(false);
  // The inline editor: null = hidden; nonce forces the focus effect to
  // re-run when re-opened at an identical position.
  const [editor, setEditor] = useState<{ left: number; top: number; nonce: number } | null>(null);
  // _editingAnnotation/_editingIsNew (js:344-345) — a ref because the
  // 150ms-deferred blur save and the chart effect's hovermode gate must read
  // it outside the render cycle. Tracked by id (data is immutable here).
  const editingRef = useRef<{ id: number | string; isNew: boolean } | null>(null);
  const editorNonce = useRef(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  /** Legacy renderChart() calls that change NO data (cancel of an unchanged
   * label, save-with-same-label) still need a full re-render — they are what
   * restores hovermode after the editor disabled it (js:2295-2305). */
  const [renderNonce, setRenderNonce] = useState(0);

  const dataRef = useRef<PsyChartData | null>(null);
  dataRef.current = data;
  /** Last-fetch-wins guard so an out-of-order response never lands. */
  const fetchSeq = useRef(0);
  const canvasRef = useRef<PlotlyChartNode | null>(null);
  const { plotly, failed: plotlyFailed } = usePlotly();
  const dispatch = useAppDispatch();

  /** Immutable equivalent of legacy's in-place `this.data.annotations`
   * edits + renderChart(): a fresh data object retriggers the chart effect. */
  const setAnnotations = useCallback(
    (fn: (anns: PsyAnnotation[]) => PsyAnnotation[]) => {
      setData((d) => (d ? { ...d, annotations: fn(d.annotations || []) } : d));
    },
    []
  );

  // ---- show / hide / minimize / restore (js:349-419) --------------------

  const show = useCallback(() => {
    setPsyPopupState('open');
    if (!ahuId) {
      setActive(true);
      setStatus('Open an AHU first to plot its air conditions.');
      return;
    }
    // Open immediately with a spinner rather than waiting on the fetch —
    // instant feedback instead of a several-second dead click (js:359-364).
    setLoading(true);
    setLoadFailed(false);
    setActive(true);
    const seq = ++fetchSeq.current;
    fetchChartData(ahuId)
      .then((resp) => {
        if (seq !== fetchSeq.current) return;
        setData(resp);
        setLoading(false);
        // Legacy leaves #psy-status-text alone on success — not cleared.
      })
      .catch(() => {
        if (seq !== fetchSeq.current) return;
        setLoading(false);
        setLoadFailed(true);
      });
  }, [ahuId]);

  const hide = useCallback(() => {
    setPsyPopupState('close');
    setActive(false);
    setIconActive(false);
  }, []);

  const minimize = useCallback(() => {
    setPsyPopupState('minimize');
    setActive(false);
    setIconActive(true);
  }, []);

  /** Silent background refresh — one of the TWO sanctioned fetch points.
   * No-op until the chart has loaded once; silent on failure (js:541-562). */
  const refreshChartData = useCallback(() => {
    if (!ahuId || !dataRef.current) return;
    const seq = ++fetchSeq.current;
    fetchChartData(ahuId)
      .then((resp) => {
        if (seq !== fetchSeq.current) return;
        setData(resp);
      })
      .catch(() => {
        /* background refresh — a transient error shouldn't surface (js:544-547) */
      });
  }, [ahuId]);

  /** Reopen from the floating icon or the header button: fast-path open with
   * whatever's on screen, then refresh in the background; a shell-only mount
   * (minimize replay) falls back to a full show() (js:398-419). */
  const restore = useCallback(() => {
    setIconActive(false);
    if (!dataRef.current) {
      show();
      return;
    }
    setPsyPopupState('open');
    setActive(true);
    refreshChartData();
  }, [show, refreshChartData]);

  // Boot: 'show' runs the full open; 'minimized' is shell-only (icon already
  // active from initial state, no fetch — header.js:255-265).
  const bootRan = useRef(false);
  useEffect(() => {
    if (bootRan.current) return;
    bootRan.current = true;
    if (boot === 'show') show();
  }, [boot, show]);

  // Every later header-button click routes through restore() (js:404-408).
  const lastNonce = useRef(openNonce);
  useEffect(() => {
    if (openNonce === lastNonce.current) return;
    lastNonce.current = openNonce;
    restore();
  }, [openNonce, restore]);

  // ---- J2: the annotation editor ----------------------------------------

  /** closeAnnotationEditor (js:2308-2312). */
  const closeAnnotationEditor = () => {
    setEditor(null);
    editingRef.current = null;
  };

  /**
   * openAnnotationEditor (js:2283-2306): positions the editor at the dot's
   * screen location, offset up-right, clamped inside the canvas wrap; hover
   * is disabled while it's open (the editor sits where the tooltip would
   * pop) — every close path re-renders, and buildLayout's hovermode
   * 'closest' turns it back on.
   *
   * Legacy sets _editingAnnotation BEFORE the position guard (js:2284-2289)
   * — replicated: a px-less annotation still counts as "editing".
   */
  const openAnnotationEditor = (ann: PsyAnnotation, isNew: boolean) => {
    editingRef.current = { id: ann.id, isNew: !!isNew };
    const node = canvasRef.current;
    const wrap = wrapRef.current;
    const px = dataToPixel(node, ann.t_db, ann.w);
    if (!px || !wrap) return;
    const wrapRect = wrap.getBoundingClientRect();
    const left = Math.max(4, Math.min(px.clientX - wrapRect.left + 14, wrapRect.width - 210));
    const top = Math.max(4, px.clientY - wrapRect.top - 34);
    editorNonce.current += 1;
    setEditor({ left, top, nonce: editorNonce.current });
    // Stash the value for the focus effect (the input only becomes
    // focusable once React shows the editor).
    pendingEditorLabel.current = ann.label || '';
    if (node && plotly) {
      plotly.Fx.unhover(node);
      void plotly.relayout(node, { hovermode: false });
    }
  };
  const pendingEditorLabel = useRef('');

  // The legacy `.val(...).focus().select()` (js:2294) — after the editor is
  // actually displayed (a display:none input can't take focus).
  useLayoutEffect(() => {
    if (!editor) return;
    const input = inputRef.current;
    if (!input) return;
    input.value = pendingEditorLabel.current;
    input.focus();
    input.select();
  }, [editor]);

  /** saveAnnotationEditor (js:2325-2343): an empty label falls back to
   * "State Point" (required server-side) rather than discarding the point;
   * a new point always persists, an existing one only when changed. */
  const saveAnnotationEditor = () => {
    const editing = editingRef.current;
    if (!editing) return;
    const label = (inputRef.current?.value ?? '').trim() || 'State Point';
    closeAnnotationEditor();
    const ann = (dataRef.current?.annotations || []).find((a) => a.id === editing.id);
    if (!ann) return;
    const changed = label !== ann.label;
    if (changed) {
      setAnnotations((anns) => anns.map((a) => (a.id === editing.id ? { ...a, label } : a)));
    } else {
      // Legacy renderCharts regardless (js:2341) — restores hovermode.
      setRenderNonce((n) => n + 1);
    }
    if (editing.isNew || changed) persistAnnotation({ ...ann, label }, editing.isNew);
  };
  /** Fresh-closure handle for the 150ms-deferred blur save. */
  const saveRef = useRef(saveAnnotationEditor);
  saveRef.current = saveAnnotationEditor;

  /** cancelAnnotationEditor (js:2314-2323): Escape — a brand-new unnamed
   * point is discarded; an existing one keeps its label. */
  const cancelAnnotationEditor = () => {
    const editing = editingRef.current;
    closeAnnotationEditor();
    if (editing && editing.isNew) {
      setAnnotations((anns) => anns.filter((a) => a.id !== editing.id));
    } else {
      setRenderNonce((n) => n + 1); // legacy renderChart() (js:2322)
    }
  };

  /**
   * persistAnnotation (js:2345-2383). t_wb/rh/h aren't user-picked — the
   * server derives all three from t_db/w at the chart's current elevation,
   * so the response merges back and re-renders so the hover has them.
   *
   * ★ DELIBERATE DEVIATION from legacy: legacy merged ONLY id + t_wb
   * (js:2364-2368/2377-2380), despite its own comment claiming it merged
   * the full response — so a just-added state point's tooltip showed "—"
   * for RH and h until the next chart_data refetch. We merge the whole
   * derived state, so the tooltip is complete as soon as the point saves.
   *
   * No .fail handlers in legacy — failures are silent and the point stays
   * local-only; preserved.
   */
  const persistAnnotation = (ann: PsyAnnotation, isNew: boolean) => {
    if (!ahuId) return;
    const elevation = dataRef.current?.elevation_ft ?? 0;
    /** The server-derived half of the state; undefined keys are skipped so a
     * partial response can never blank a value the client already has. */
    const derived = (saved: PsyAnnotation): Partial<PsyAnnotation> => ({
      ...(saved.t_wb !== undefined ? { t_wb: saved.t_wb } : {}),
      ...(saved.rh !== undefined ? { rh: saved.rh } : {}),
      ...(saved.h !== undefined ? { h: saved.h } : {}),
    });
    if (isNew) {
      createAnnotation(ahuId, elevation, { label: ann.label, t_db: ann.t_db, w: ann.w })
        .then((saved) => {
          if (!saved) return;
          setAnnotations((anns) =>
            anns.map((a) => (a.id === ann.id ? { ...a, id: saved.id, ...derived(saved) } : a))
          );
        })
        .catch(() => {});
    } else {
      patchAnnotationLabel(ahuId, ann.id, elevation, ann.label)
        .then((saved) => {
          if (!saved) return;
          setAnnotations((anns) =>
            anns.map((a) => (a.id === ann.id ? { ...a, ...derived(saved) } : a))
          );
        })
        .catch(() => {});
    }
  };

  /** addAnnotation (js:2265-2278): one point per "Add State Point" click —
   * annotation mode expires the moment the click lands. */
  const addAnnotation = (t_db: number, w: number) => {
    const ann: PsyAnnotation = { id: `tmp-${Date.now()}`, label: '', t_db, w };
    setAnnotations((anns) => anns.concat([ann]));
    setAnnotationMode(false);
    setStatus('');
    openAnnotationEditor(ann, true);
  };

  /** onCanvasClick (js:2237-2255): an existing dot/label opens for editing
   * regardless of mode; empty background adds only when armed. */
  const onCanvasClick = (e: React.MouseEvent) => {
    const node = canvasRef.current;
    const existing = findAnnotationNear(
      node,
      dataRef.current?.annotations || [],
      e.clientX,
      e.clientY,
      14
    );
    if (existing) {
      if (editingRef.current && editingRef.current.id !== existing.id) saveAnnotationEditor();
      openAnnotationEditor(existing, false);
      return;
    }
    if (!annotationMode) return;
    // Commit the current edit before starting a new point — otherwise the
    // native blur this click also triggers races the new editor (js:2248-2251).
    if (editingRef.current) saveAnnotationEditor();
    const pt = pixelToData(node, e.clientX, e.clientY);
    if (!pt) return;
    addAnnotation(pt.t_db, pt.w);
  };

  /** onCanvasDoubleClick (js:2257-2263): onCanvasClick already opened the
   * editor on the first click; this just stops native SVG text selection. */
  const onCanvasDoubleClick = (e: React.MouseEvent) => {
    if (findAnnotationNear(canvasRef.current, dataRef.current?.annotations || [], e.clientX, e.clientY, 14)) {
      e.preventDefault();
    }
  };

  /** toggleAnnotationMode (js:632-639). */
  const toggleAnnotationMode = () => {
    if (editingRef.current) saveAnnotationEditor();
    const next = !annotationMode;
    setAnnotationMode(next);
    setStatus(next ? 'Click the chart to drop a state point.' : '');
  };

  /** clearAnnotations (js:641-657): optimistic clear with rollback. */
  const clearAnnotations = () => {
    const d = dataRef.current;
    if (!d || !(d.annotations || []).length) return;
    closeAnnotationEditor();
    const prev = d.annotations;
    setAnnotations(() => []);
    if (!ahuId) return;
    clearAllAnnotations(ahuId).catch(() => {
      setAnnotations(() => prev);
      setStatus('Could not clear state points.');
    });
  };

  /** onAnnotationDeleteClick (js:2385-2399): local removal always; the
   * server DELETE only for a persisted row (numeric id — a "tmp-…" string
   * means the POST hasn't landed). Fire-and-forget like legacy. */
  const onAnnotationDeleteClick = () => {
    const editing = editingRef.current;
    if (!editing) return;
    const ann = (dataRef.current?.annotations || []).find((a) => a.id === editing.id);
    closeAnnotationEditor();
    if (!ann) return;
    setAnnotations((anns) => anns.filter((a) => a.id !== ann.id));
    if (!editing.isNew && typeof ann.id !== 'string' && ahuId) {
      void deleteAnnotation(ahuId, ann.id);
    }
  };

  /** onAnnotationInputKeydown (js:2401-2409). */
  const onAnnotationInputKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveAnnotationEditor();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelAnnotationEditor();
    }
  };

  /** onAnnotationInputBlur (js:2411-2420): deferred so a Delete-button click
   * (which also blurs) resolves first — its handler nulls the editing state
   * and this becomes a safe no-op instead of racing a save against a delete. */
  const onAnnotationInputBlur = () => {
    window.setTimeout(() => {
      if (editingRef.current) saveRef.current();
    }, 150);
  };

  // ---- the chart itself (renderChart js:659-704) ------------------------

  const staleBySeason = useMemo(() => computeStaleBySeason(data), [data]);

  useEffect(() => {
    const node = canvasRef.current;
    if (!plotly || !data || !node) return undefined;
    const traces = buildTraces(data, season, staleBySeason, streamVisibility);
    const layout = buildLayout(data.axes, data.curves);
    // The editor may be open while a re-render lands (e.g. the new dot of
    // addAnnotation) — hover must STAY off until the editor closes; every
    // close path re-renders, restoring buildLayout's 'closest' (js:2295-2305).
    if (editingRef.current) (layout as Record<string, unknown>).hovermode = false;
    let cancelled = false;
    void plotly
      .react(node, traces as unknown[], layout, { ...PLOT_CONFIG })
      .then(() => {
        // After Plotly has actually drawn — real rendered label sizes.
        if (cancelled) return;
        declutterLabels(plotly, node, traces);
        // The plot is on screen: drop the plot-area spinner (see chartReady).
        setChartReady(true);
      });
    // bindAnnotationHoverLabelToggle (js:853-872): hide a green annotation's
    // text label while its tooltip shows, restore on unhover — direct DOM
    // opacity, no restyle, bound ONCE per node. The Nth .textpoint under a
    // trace's <g class="trace"> is that trace's Nth data point (index join),
    // and curveNumber picks the trace under .scatterlayer.
    const nodeWithFlag = node as PlotlyChartNode & { _psyAnnotationHoverBound?: boolean };
    if (node.on && !nodeWithFlag._psyAnnotationHoverBound) {
      nodeWithFlag._psyAnnotationHoverBound = true;
      const annotationLabelEl = (pt: PlotlyHoverPoint | undefined): HTMLElement | null => {
        if (!pt || !pt.data || pt.data.name !== 'Annotations') return null;
        const traceEl = node.querySelectorAll('.scatterlayer .trace')[pt.curveNumber];
        if (!traceEl) return null;
        return (traceEl.querySelectorAll('.textpoint')[pt.pointNumber] as HTMLElement) || null;
      };
      node.on('plotly_hover', (evt) => {
        const points = (evt as { points?: PlotlyHoverPoint[] }).points;
        const el = annotationLabelEl(points && points[0]);
        if (el) el.style.opacity = '0';
      });
      node.on('plotly_unhover', (evt) => {
        const points = (evt as { points?: PlotlyHoverPoint[] }).points;
        const el = annotationLabelEl(points && points[0]);
        if (el) el.style.opacity = '';
      });
    }
    return () => {
      cancelled = true;
    };
  }, [plotly, data, season, staleBySeason, streamVisibility, renderNonce]);

  // ---- header-control handlers ------------------------------------------

  /** js:564-568 — client-side filter, NO refetch (both seasons ship in
   * every payload; survey §C5 item 5). */
  const onSeasonChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value as PsySeasonFilter;
    setSeason(v);
    if (v !== 'both') setSidebarSeason(v);
  };

  /** A legend air-stream checkbox. Client-side filter, NO refetch, same as
   * the season selector above. Unchecking all three is allowed — it leaves
   * the curve grid and any state points the user annotated, which is a
   * legitimate thing to want and one click away from undoing. The sidebar
   * table is untouched: it is the unit's reference listing, and it already
   * has its own per-path collapse. */
  const onStreamToggle = useCallback((stream: PsyToggleableStream) => {
    setStreamVisibility((prev) => {
      const next = { ...prev, [stream]: !prev[stream] };
      setPsyStreamVisibility(next);
      return next;
    });
  }, []);

  /** js:584-592 — single selection; clicking the selected row clears it. */
  const onRowClick = (row: PsySidebarRow) => {
    if (!row.component_id) return;
    const key = rowKey(row.component_kind, row.component_id);
    setSelectedRowKey((prev) => (prev === key ? null : key));
  };

  const onPathToggle = (pathName: string) => {
    setCollapsedPaths((prev) => ({ ...prev, [pathName]: !prev[pathName] }));
  };

  /**
   * jumpToSection (js:1458-1538), completed in J5: close this dialog, hand
   * the fresh entering conditions off via PSY_PENDING_RERATE_KEY, select the
   * section on the board, and open its rating dialog. `handoff: false` keeps
   * everything but the hand-off — see jumpToSection/openSectionDialog below.
   *
   * - The handoff merges BOTH seasons' stale entries (js:1462-1480 — a
   *   section/bridge can need re-rate in summer AND winter independently;
   *   summer's bare keys never collide with winter's `_winter` keys). No
   *   fields → the key is REMOVED (js:1497-1499).
   * - Legacy then DOM-clicked the board's "Rating" label (js:1500-1537).
   *   React re-expression (survey §C5 item 1, the FormsPanel.showRating
   *   precedent): `select:section` on the bus (selection + components tab —
   *   AhuCanvas consumer → Redux) then `openRatingRequest(sectionId)` — the
   *   mounted component form is the id authority and fires its own
   *   `rating_mode:activate`; the request token is consumed even by a form
   *   that mounts AFTER the dispatch, and the matcher accepts the bridge's
   *   section2 (useComponentForm:503-511). The legacy 100ms settle + the
   *   already-open-dialog branch (rating_mode:hide, then 300ms —
   *   js:1526-1536) are kept.
   */
  const openSectionRating = useCallback(
    (sectionId: number, handoff: boolean) => {
      if (!sectionId) return;
      hide();
      // `handoff` false is the plain black RE-RATE button (requested
      // 2026-10-16, see rowCanOpenDialog): land on the dialog and change
      // nothing. The key is still CLEARED rather than left alone — a row
      // can be clean in the season on screen while another section is
      // stale, and a stale key left behind would be consumed by whichever
      // selector matches it next.
      const stale = computeStaleBySeason(dataRef.current);
      const summerUpdate = handoff ? stale.summer[sectionId] : undefined;
      const winterUpdate = handoff ? stale.winter[sectionId] : undefined;
      const fields = {
        ...(summerUpdate && summerUpdate.fields),
        ...(winterUpdate && winterUpdate.fields),
      } as Record<string, number>;
      if (Object.keys(fields).length) {
        const source = (summerUpdate ?? winterUpdate)!;
        writePendingRerate({
          section_id: sectionId,
          target: source.target,
          component_id: source.component_id,
          fields,
        });
      } else {
        clearPendingRerate();
      }
      ahuBoardBus.emit('select:section', { section: { id: sectionId }, tab: 'components' });
      window.setTimeout(() => {
        const fire = () => dispatch(openRatingRequest(sectionId));
        const mode = (store.getState() as RootState).ahuBoard.boardMode;
        if (mode.ratingActive || mode.flipped) {
          // a rating dialog is already open — close it first so we land on
          // a fresh one (the legacy 300ms mirrors component_selector.js's
          // own hide-then-rerender delay, js:1526-1536).
          ahuBoardBus.emit('rating_mode:hide');
          window.setTimeout(fire, 300);
        } else {
          fire();
        }
      }, 100);
    },
    [hide, dispatch]
  );

  /** The red RERATE COMPONENT button — jump WITH the re-rate handoff. */
  const jumpToSection = useCallback(
    (sectionId: number) => openSectionRating(sectionId, true),
    [openSectionRating]
  );

  /** The black one — same jump, no handoff, so the dialog opens on the
   * values it already holds and re-rates nothing. */
  const openSectionDialog = useCallback(
    (sectionId: number) => openSectionRating(sectionId, false),
    [openSectionRating]
  );

  /**
   * J5 — synchronizeRow (js:1361-1450): the sidebar SYNCHRONIZE button —
   * PATCHes (UDA: POSTs) whichever season(s) are actually stale straight
   * onto the underlying model, no dialog involved.
   *
   * - UDA (electric_heater/humidifier): the backend never populates a
   *   fields map for UDA divergence, so the payload is built from this
   *   row's own entering/leaving — safe because a UDA's
   *   {eadb,eawb,ladb,lawb}_temp_<season> are genuinely independent
   *   per-season columns (js:1365-1376/:1395-1408).
   * - Everything else: reuse the backend's own fresh field map for this
   *   section (staleBySeason — the same map jumpToSection hands off) rather
   *   than re-deriving from the row — required for correctness on a fan,
   *   whose entering is ONE shared column across both seasons
   *   (js:1378-1388). Summer's bare eadb/eawb keys are normalized to the
   *   `_summer` aliases too (js:1414-1423).
   * - On done: full refetch (synchronizing can un-stick downstream sections
   *   server-side — js:1438-1443); a sanctioned explicit-refresh point.
   */
  const [syncRowBusy, setSyncRowBusy] = useState(false);
  const synchronizeRow = useCallback(
    (row: PsySidebarRow) => {
      if (!row.section_id || !row.component_id) return;
      const isUda = !!UDA_KINDS[row.component_kind ?? ''];
      const payload: Record<string, number> = {};
      if (isUda) {
        (['summer', 'winter'] as const).forEach((seasonKey) => {
          const cond = row[seasonKey];
          if (!cond || !cond.stale) return;
          const ent = cond.entering || {};
          const leav = cond.leaving || {};
          const suffix = `_${seasonKey}`;
          if (ent.t_db != null) payload[`eadb_temp${suffix}`] = ent.t_db;
          if (ent.t_wb != null) payload[`eawb_temp${suffix}`] = ent.t_wb;
          if (leav.t_db != null) payload[`ladb_temp${suffix}`] = leav.t_db;
          if (leav.t_wb != null) payload[`lawb_temp${suffix}`] = leav.t_wb;
        });
      } else {
        const stale = computeStaleBySeason(dataRef.current);
        const summerEntry = stale.summer[row.section_id];
        const winterEntry = stale.winter[row.section_id];
        Object.assign(payload, summerEntry && summerEntry.fields, winterEntry && winterEntry.fields);
        (['eadb_temp', 'eawb_temp'] as const).forEach((field) => {
          if (payload[field] !== undefined) payload[`${field}_summer`] = payload[field];
        });
      }
      if (!Object.keys(payload).length) return;
      const url = isUda
        ? `/api/v1.0/ahu/user_defined_accessories/${row.component_id}/commit_air_conditions`
        : `/api/v1.0/ahu/sections/${row.section_id}/commit_air_conditions`;
      setSyncRowBusy(true);
      postCommitAirConditions(url, payload)
        .then(() => {
          setSyncRowBusy(false);
          refreshChartData();
        })
        .catch(() => {
          setSyncRowBusy(false);
        });
    },
    [refreshChartData]
  );

  // ---- J4: the synchronize toggle (onSyncToggle js:607-630) --------------

  // Legacy keeps its own copy, assigned on every fetch (js:368/:557) and on
  // toggle success (js:618); mirrored as state that follows `data`.
  const [syncOn, setSyncOn] = useState(false);
  useEffect(() => {
    setSyncOn(!!data?.synchronize_air_conditions);
  }, [data]);

  // The box is controlled by `syncOn`, which only moves on PATCH success
  // (js:618) — so for as long as the PATCH is in flight it keeps showing the
  // OLD value and the click reads as ignored. That PATCH is seconds on a
  // hybrid unit, and nothing disabled the box, so an impatient user clicked
  // again and again: four PATCHes of AHU 7164 for one intended change,
  // serialized behind each other's per-AHU base-group lock (6.0 s, 10.9 s,
  // 18.2 s, 10.8 s as measured), the last of them sending the opposite value.
  // Hold the box while its own request is outstanding -- same `*Busy` shape as
  // `syncRowBusy` above, and `disabled` is what makes the wait visible.
  const [syncBusy, setSyncBusy] = useState(false);

  const onSyncToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (!ahuId || syncBusy) return;
    setSyncBusy(true);
    patchSynchronizeAirConditions(ahuId, checked)
      .then(() => {
        setSyncBusy(false);
        setSyncOn(checked);
        // ★ Cache coherence (deliberate, documented deviation): legacy's raw
        // PATCH left the BOARD's own AHU model stale until reload (board and
        // chart hold two disconnected copies — fn/map.js:704-738), so the
        // board-side sync gates kept the old value. React has one store —
        // merge the flag into the getAhuUnit cache so the J4 commit gates
        // see it immediately. Success-only, so a failed PATCH can't desync
        // the cache.
        dispatch(
          ahuBoardApi.util.updateQueryData('getAhuUnit', ahuId, (draft) => {
            (draft as Record<string, unknown>).synchronize_air_conditions = checked;
          })
        );
        // The OFF→ON transition just flagged whichever components' conditions
        // no longer match (propagate_unit's void_on_change) — refetch so the
        // red rows reflect it immediately (js:619-625). One of the sanctioned
        // explicit-refresh fetch points.
        refreshChartData();
      })
      .catch(() => {
        setSyncBusy(false);
        // js:626-629 — the controlled box never moved; surface the status.
        setStatus('Could not update sync setting.');
      });
  };

  // ---- derived shell values ---------------------------------------------

  const elevation = data ? data.elevation_ft : 0;
  // js:369-373 — only set once data lands; hidden while loading.
  const projectAhuLabel = data
    ? [data.project_name, data.ahu_tag].filter(Boolean).join(' : ')
    : '';

  return (
    <div className="psychrometric-chart-host">
      <div
        className={`psychrometric-chart-popup popup-overlay${active ? ' active' : ''}`}
        onClick={(e) => {
          // js:388-390 — only a click on the overlay itself closes.
          if (e.target === e.currentTarget) hide();
        }}
      >
        <div className="psychrometric-chart-dialog">
          <div className="psychrometric-chart-header">
            <div className="psychrometric-chart-title-block">
              <div className="psychrometric-chart-title">Kinetix Air - Psychrometrics</div>
              {projectAhuLabel ? (
                <div className="psychrometric-chart-subtitle">{projectAhuLabel}</div>
              ) : null}
            </div>
            <div className="psychrometric-chart-controls">
              <label className="psy-control" title="Set on the project's Main page — not editable here">
                <span>Elevation (ft)</span>
                {/* Display-only — the project's own value, fetched fresh,
                    never sent back (tmpl:11-14, survey gotcha 7). */}
                <input type="number" id="psy-elevation-input" value={elevation} disabled readOnly />
              </label>
              <label className="psy-control">
                <span>Season</span>
                <select
                  id="psy-season-select"
                  value={season}
                  disabled={loading}
                  onChange={onSeasonChange}
                >
                  <option value="both">Summer + Winter</option>
                  <option value="summer">Summer</option>
                  <option value="winter">Winter</option>
                </select>
              </label>
              <label className="psy-control psy-toggle">
                <span>Synchronize</span>
                <input
                  type="checkbox"
                  className="ka-has-tooltip"
                  id="psy-sync-toggle"
                  tooltip="Synchronize air conditions from one section to the next"
                  checked={syncOn}
                  disabled={loading || syncBusy}
                  onChange={onSyncToggle}
                />
              </label>
              <button
                type="button"
                className="psy-download-btn ka-has-tooltip"
                id="psy-download-btn"
                tooltip="Download psychrometric chart + side bar table"
                onClick={() => {
                  // J3a — exportChartAsPng (js:886-918): Plotly.toImage +
                  // the hand-drawn sidebar table for whichever season tab is
                  // showing, one composite PNG. Guarded like legacy (node
                  // check only; a plot-less node fails into console.error).
                  if (plotly) {
                    exportChartAsPng(plotly, canvasRef.current, dataRef.current, sidebarSeason);
                  }
                }}
              >
                <i className="psy-download-icon" />
                <span>Download</span>
              </button>
              <button type="button" className="psy-minimize-btn" title="Minimize" onClick={minimize}>
                &minus;
              </button>
              <button type="button" className="psy-close-btn" title="Close" onClick={hide}>
                Close
              </button>
            </div>
          </div>
          <div className="psychrometric-chart-body">
            {loading || !data ? (
              // Nothing to draw yet. Loading gets the spinner alone (no
              // caption — the spinner says it); the two no-data endings (a
              // failed fetch, the no-AHU guard) get a line of text, since an
              // empty dialog with neither reads as broken.
              <div className="psy-loading-overlay">
                {loading ? (
                  <div className="preloader dotted-preloader larger active" />
                ) : (
                  <div className="psy-loading-message">
                    {loadFailed
                      ? 'Could not load the psychrometric data. Close the window and try again.'
                      : 'Open an AHU first to plot its air conditions.'}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="psychrometric-chart-canvas-wrap" ref={wrapRef}>
                  {/* Legacy binds click/dblclick on the canvas node itself
                      (events map js:306-307) — annotation add/edit hit-testing
                      runs off plain DOM clicks, not plotly_click (js:2152-2162). */}
                  {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
                  <div
                    id="psychrometric-chart-canvas"
                    ref={(el) => {
                      canvasRef.current = el as PlotlyChartNode | null;
                    }}
                    onClick={onCanvasClick}
                    onDoubleClick={onCanvasDoubleClick}
                  />
                  {/* The sidebar renders straight from the payload, but the
                      plot waits on the 1 MB vendored Plotly — cover that gap
                      rather than show a white rectangle. Dropped on a failed
                      script load too, or the spinner would never stop. */}
                  {!chartReady && !plotlyFailed ? (
                    <div className="preloader dotted-preloader larger active psy-chart-preloader" />
                  ) : null}
                  {/* The inline annotation editor (tmpl:43-46) — shown at the
                      dot's screen position, offset up-right, clamped inside
                      the wrap (js:2283-2293). */}
                  <div
                    id="psy-annotation-editor"
                    className="psy-annotation-editor"
                    style={
                      editor
                        ? { left: `${editor.left}px`, top: `${editor.top}px`, display: 'block' }
                        : { display: 'none' }
                    }
                  >
                    <input
                      type="text"
                      id="psy-annotation-input"
                      maxLength={30}
                      placeholder="Label this state point"
                      ref={inputRef}
                      onKeyDown={onAnnotationInputKeydown}
                      onBlur={onAnnotationInputBlur}
                    />
                    <button
                      type="button"
                      id="psy-annotation-delete"
                      className="psy-annotation-delete-btn"
                      title="Delete"
                      onClick={onAnnotationDeleteClick}
                    >
                      <i className="psy-annotation-delete-icon" />
                    </button>
                  </div>
                </div>
                <div className="psychrometric-chart-legend">
                  <div id="psy-sidebar-sticky-header">
                    <Legend
                      data={data}
                      season={season}
                      streamVisibility={streamVisibility}
                      onStreamToggle={onStreamToggle}
                    />
                    <SidebarTabsBar
                      paths={data ? data.sidebar_paths : null}
                      chartSeason={season}
                      sidebarSeason={sidebarSeason}
                      selectedRowKey={selectedRowKey}
                      staleBySeason={staleBySeason}
                      onSeasonTab={setSidebarSeason}
                      onRerate={jumpToSection}
                      onOpenDialog={openSectionDialog}
                      onSynchronize={synchronizeRow}
                      syncBusy={syncRowBusy}
                    />
                  </div>
                  <SidebarTable
                    paths={data ? data.sidebar_paths : null}
                    chartSeason={season}
                    sidebarSeason={sidebarSeason}
                    selectedRowKey={selectedRowKey}
                    collapsedPaths={collapsedPaths}
                    onRowClick={onRowClick}
                    onPathToggle={onPathToggle}
                  />
                </div>
              </>
            )}
          </div>
          <div className="psychrometric-chart-footer">
            <div className="psy-status" id="psy-status-text">
              {status}
            </div>
            <div className="psy-actions">
              <button
                type="button"
                className={`psy-btn-annotate${annotationMode ? ' active' : ''}`}
                id="psy-toggle-annotate"
                onClick={toggleAnnotationMode}
              >
                Add State Point
              </button>
              <button
                type="button"
                className="psy-btn-clear-annotations"
                id="psy-clear-annotations"
                onClick={clearAnnotations}
              >
                Clear All State Points
              </button>
            </div>
          </div>
        </div>
      </div>
      <PsychrometricFloatingIcon active={iconActive} onRestore={restore} />
    </div>
  );
};

export default PsychrometricChartPopup;
